/**
 * MUTAGIM.SHOP — Search Page (Phase 6)
 * Page de recherche avec filtres et autocomplétion
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Search, Filter, X, ChevronDown, SlidersHorizontal, Loader2, Grid, List
} from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { ProductCardNetflix } from '../components/product/ProductCardNetflix';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import { Checkbox } from '../components/ui/checkbox';
import { Slider } from '../components/ui/slider';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function SearchPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const lang = i18n.language;
  
  // État
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [filters, setFilters] = useState({ sizes: [], colors: [], categories: [], brands: [] });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [availableBrands, setAvailableBrands] = useState([]);
  
  // Ref pour le scroll infini
  const loadMoreRef = useRef(null);
  
  // Paramètres de recherche
  const query = searchParams.get('q') || '';
  const category = searchParams.get('category') || '';
  const brand = searchParams.get('brand') || '';
  const size = searchParams.get('size') || '';
  const color = searchParams.get('color') || '';
  const minPrice = searchParams.get('min_price') || '';
  const maxPrice = searchParams.get('max_price') || '';
  const sort = searchParams.get('sort') || 'relevance';

  // Charger les produits (initial ou reset)
  const loadProducts = useCallback(async (reset = true) => {
    try {
      if (reset) {
        setLoading(true);
        setCurrentPage(1);
      } else {
        setLoadingMore(true);
      }
      
      const pageToLoad = reset ? 1 : currentPage + 1;
      const params = { page: pageToLoad, limit: 24, sort };
      if (query) params.q = query;
      if (category) params.category = category;
      if (brand) params.brand = brand;
      if (size) params.size = size;
      if (color) params.color = color;
      if (minPrice) params.min_price = parseFloat(minPrice);
      if (maxPrice) params.max_price = parseFloat(maxPrice);
      
      const response = await axios.get(`${API_URL}/api/storefront/search`, { params });
      
      const newProducts = response.data.products || [];
      const totalPages = response.data.pages || 1;
      
      if (reset) {
        setProducts(newProducts);
      } else {
        setProducts(prev => [...prev, ...newProducts]);
      }
      
      setTotal(response.data.total || 0);
      setFilters(response.data.filters || { sizes: [], colors: [], categories: [] });
      setCurrentPage(pageToLoad);
      setHasMore(pageToLoad < totalPages);
      
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [query, category, brand, size, color, minPrice, maxPrice, sort, currentPage]);

  // Charger au changement de filtres
  useEffect(() => {
    loadProducts(true);
  }, [query, category, brand, size, color, minPrice, maxPrice, sort]);

  // Charger les marques disponibles
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/brands?with_products=true`);
        setAvailableBrands(response.data.brands || []);
      } catch (err) {
        console.error('Error fetching brands:', err);
      }
    };
    fetchBrands();
  }, []);

  // Scroll infini - Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          loadProducts(false);
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, loadProducts]);

  // Mettre à jour un paramètre
  const updateParam = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    // Reset page quand on change les filtres
    if (key !== 'page') {
      newParams.set('page', '1');
    }
    setSearchParams(newParams);
  };

  // Effacer tous les filtres
  const clearFilters = () => {
    const newParams = new URLSearchParams();
    if (query) newParams.set('q', query);
    setSearchParams(newParams);
  };

  // Compter les filtres actifs
  const activeFiltersCount = [category, brand, size, color, minPrice, maxPrice].filter(Boolean).length;

  // Options de tri
  const sortOptions = [
    { value: 'relevance', label: t('search.sort.relevance', 'Pertinence') },
    { value: 'newest', label: t('search.sort.newest', 'Nouveautés') },
    { value: 'price_asc', label: t('search.sort.priceAsc', 'Prix croissant') },
    { value: 'price_desc', label: t('search.sort.priceDesc', 'Prix décroissant') },
    { value: 'rating', label: t('search.sort.rating', 'Meilleures notes') },
  ];

  return (
    <div className="min-h-screen bg-mutagim-dark" data-testid="search-page">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Titre et résumé */}
        <div className="mb-6">
          {brand ? (
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
              {t('search.brandProducts', 'Produits')} {brand}
            </h1>
          ) : query ? (
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
              {t('search.resultsFor', 'Résultats pour')} "{query}"
            </h1>
          ) : (
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
              {t('search.allProducts', 'Tous les produits')}
            </h1>
          )}
          <p className="text-gray-400">
            {total} {t('search.productsFound', 'produits trouvés')}
          </p>
        </div>
        
        {/* Barre de filtres */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6 pb-6 border-b border-gray-800">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Bouton filtres mobile */}
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="outline" 
                  className="border-gray-700 text-white lg:hidden"
                >
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  {t('search.filters', 'Filtres')}
                  {activeFiltersCount > 0 && (
                    <Badge className="ml-2 bg-mutagim-red">{activeFiltersCount}</Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-mutagim-dark border-gray-800 w-80">
                <SheetHeader>
                  <SheetTitle className="text-white">{t('search.filters', 'Filtres')}</SheetTitle>
                </SheetHeader>
                <FilterPanel 
                  filters={filters}
                  category={category}
                  brand={brand}
                  size={size}
                  color={color}
                  minPrice={minPrice}
                  maxPrice={maxPrice}
                  updateParam={updateParam}
                  clearFilters={clearFilters}
                  lang={lang}
                  t={t}
                  availableBrands={availableBrands}
                />
              </SheetContent>
            </Sheet>
            
            {/* Filtres actifs */}
            {category && (
              <Badge variant="secondary" className="bg-gray-800 text-white">
                {filters.categories.find(c => c.slug === category)?.name?.[lang] || category}
                <button onClick={() => updateParam('category', '')} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            {brand && (
              <Badge variant="secondary" className="bg-mutagim-red/20 border-mutagim-red text-white">
                {t('search.brand', 'Marque')}: {brand}
                <button onClick={() => updateParam('brand', '')} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            {size && (
              <Badge variant="secondary" className="bg-gray-800 text-white">
                {t('search.size', 'Taille')}: {size}
                <button onClick={() => updateParam('size', '')} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            {color && (
              <Badge variant="secondary" className="bg-gray-800 text-white">
                {t('search.color', 'Couleur')}: {color}
                <button onClick={() => updateParam('color', '')} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            
            {activeFiltersCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm"
                className="text-gray-400 hover:text-white"
                onClick={clearFilters}
              >
                {t('search.clearAll', 'Tout effacer')}
              </Button>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {/* Tri */}
            <Select value={sort} onValueChange={(v) => updateParam('sort', v)}>
              <SelectTrigger className="w-[180px] bg-gray-900 border-gray-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-700">
                {sortOptions.map(opt => (
                  <SelectItem 
                    key={opt.value} 
                    value={opt.value}
                    className="text-white hover:bg-gray-800"
                  >
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Vue grille/liste */}
            <div className="hidden md:flex border border-gray-700 rounded-lg overflow-hidden">
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "rounded-none",
                  viewMode === 'grid' ? "bg-gray-800 text-white" : "text-gray-400"
                )}
                onClick={() => setViewMode('grid')}
              >
                <Grid className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "rounded-none",
                  viewMode === 'list' ? "bg-gray-800 text-white" : "text-gray-400"
                )}
                onClick={() => setViewMode('list')}
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
        
        {/* Contenu principal */}
        <div className="flex gap-8">
          {/* Sidebar filtres - Desktop */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <FilterPanel 
              filters={filters}
              category={category}
              size={size}
              color={color}
              minPrice={minPrice}
              maxPrice={maxPrice}
              updateParam={updateParam}
              clearFilters={clearFilters}
              lang={lang}
              t={t}
            />
          </aside>
          
          {/* Grille produits */}
          <div className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-mutagim-red" />
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20">
                <Search className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg mb-4">
                  {t('search.noResults', 'Aucun produit trouvé')}
                </p>
                <Button onClick={clearFilters} variant="outline" className="border-gray-700 text-white">
                  {t('search.clearFilters', 'Effacer les filtres')}
                </Button>
              </div>
            ) : (
              <>
                <div className={cn(
                  "grid gap-4",
                  viewMode === 'grid' 
                    ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
                    : "grid-cols-1"
                )}>
                  {products.map((product) => (
                    <ProductCardNetflix 
                      key={product.id} 
                      product={product}
                      className={viewMode === 'list' ? "flex-row" : ""}
                    />
                  ))}
                </div>
                
                {/* Scroll infini - Loader */}
                <div ref={loadMoreRef} className="flex items-center justify-center py-8">
                  {loadingMore && (
                    <Loader2 className="w-6 h-6 animate-spin text-mutagim-red" />
                  )}
                  {!hasMore && products.length > 0 && (
                    <p className="text-gray-500 text-sm">
                      {t('search.endOfResults', 'Fin des résultats')}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

// Composant panneau de filtres
function FilterPanel({ filters, category, brand, size, color, minPrice, maxPrice, updateParam, clearFilters, lang, t, availableBrands }) {
  return (
    <div className="space-y-6 py-4">
      {/* Marques */}
      {availableBrands?.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-white font-medium">{t('search.brand', 'Marque')}</h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {availableBrands.map((b) => (
              <label 
                key={b.id || b.name}
                className="flex items-center gap-2 text-gray-300 hover:text-white cursor-pointer"
              >
                <Checkbox
                  checked={brand === b.name}
                  onCheckedChange={(checked) => updateParam('brand', checked ? b.name : '')}
                />
                <span className="flex items-center gap-2">
                  {b.logo_url && (
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}${b.logo_url}`} 
                      alt={b.name}
                      className="w-5 h-5 object-contain rounded"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                  {b.name}
                  <span className="text-gray-500 text-xs">({b.product_count})</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Catégories */}
      {filters.categories?.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-white font-medium">{t('search.category', 'Catégorie')}</h3>
          <div className="space-y-2">
            {filters.categories.map((cat) => (
              <label 
                key={cat.slug}
                className="flex items-center gap-2 text-gray-300 hover:text-white cursor-pointer"
              >
                <Checkbox
                  checked={category === cat.slug}
                  onCheckedChange={(checked) => updateParam('category', checked ? cat.slug : '')}
                />
                <span>{cat.name?.[lang] || cat.slug}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      
      {/* Tailles */}
      {filters.sizes?.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-white font-medium">{t('search.size', 'Taille')}</h3>
          <div className="flex flex-wrap gap-2">
            {filters.sizes.map((s) => (
              <button
                key={s}
                className={cn(
                  "px-3 py-1 rounded border text-sm transition-all",
                  size === s
                    ? "border-mutagim-red bg-mutagim-red/20 text-white"
                    : "border-gray-700 text-gray-300 hover:border-gray-500"
                )}
                onClick={() => updateParam('size', size === s ? '' : s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Couleurs */}
      {filters.colors?.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-white font-medium">{t('search.color', 'Couleur')}</h3>
          <div className="flex flex-wrap gap-2">
            {filters.colors.map((c) => (
              <button
                key={c}
                className={cn(
                  "px-3 py-1 rounded border text-sm transition-all",
                  color === c
                    ? "border-mutagim-red bg-mutagim-red/20 text-white"
                    : "border-gray-700 text-gray-300 hover:border-gray-500"
                )}
                onClick={() => updateParam('color', color === c ? '' : c)}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Prix */}
      <div className="space-y-3">
        <h3 className="text-white font-medium">{t('search.priceRange', 'Fourchette de prix')}</h3>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            placeholder="Min"
            value={minPrice}
            onChange={(e) => updateParam('min_price', e.target.value)}
            className="bg-gray-800 border-gray-700 text-white w-24"
          />
          <span className="text-gray-400">-</span>
          <Input
            type="number"
            placeholder="Max"
            value={maxPrice}
            onChange={(e) => updateParam('max_price', e.target.value)}
            className="bg-gray-800 border-gray-700 text-white w-24"
          />
        </div>
      </div>
      
      {/* Bouton effacer */}
      <Button 
        variant="outline" 
        className="w-full border-gray-700 text-white"
        onClick={clearFilters}
      >
        {t('search.clearAll', 'Effacer les filtres')}
      </Button>
    </div>
  );
}

export default SearchPage;
